// Vídeo en trozos de 1 s encadenados con firmas periódicas (ADR 0004 §4.3 y desviación 6):
// firma del estado de la cadena en upto = 4, 9, … (< n−1) y firma final en upto = n−1 con la raíz.
import type { CaptureClock } from './clock.js';
import { sha256Hex } from './crypto.js';
import { HashChain, VIDEO_SIGN_EVERY } from './HashChain.js';
import { videoMessage, type CaptureSigner } from './SessionKeys.js';
import type { CaptureVideo, CaptureVideoChunk, CaptureVideoKind } from './types.js';

/** Fuente de trozos de vídeo (MediaRecorder en el navegador; sintética en pruebas). */
export interface ChunkSource {
  /** Empieza a grabar y entrega cada trozo con su `t_mono` (µs) al cerrarse. */
  start(onChunk: (bytes: Uint8Array, t_mono: number) => void, timesliceMs: number): void;
  /** Para la grabación; se resuelve cuando se ha entregado el último trozo. */
  stop(): Promise<void>;
  /** Tipo MIME de los trozos. */
  readonly mimeType: string;
}

/** Trozo grabado: entrada del bundle y bytes para subir. */
export interface RecordedChunk {
  meta: CaptureVideoChunk;
  bytes: Uint8Array;
}

export interface VideoRecorderOptions {
  sessionId: string;
  serverNonce: string;
  kind: CaptureVideoKind;
  signer: CaptureSigner;
  /** Duración de cada trozo (ms). Por defecto 1000. */
  timesliceMs?: number;
  /** Se llama con cada trozo ya encadenado (para subirlo en cuanto existe). */
  onChunk?: (chunk: RecordedChunk) => void;
}

/**
 * Graba un vídeo encadenado. Cada trozo → SHA-256 + `t_mono` → eslabón de la cadena; cada 5
 * trozos se firma el estado y al terminar se firma la raíz.
 *
 * ```ts
 * const rec = new VideoRecorder(source, { sessionId, serverNonce, kind: 'challenge', signer });
 * rec.start(); …; const video = await rec.stop(); // CaptureVideo | null si no hubo trozos
 * ```
 */
export class VideoRecorder {
  private readonly chunks: RecordedChunk[] = [];
  private readonly periodic: { upto: number; hash: string; signature: string }[] = [];
  private chainP: Promise<HashChain>;
  /** Serializa el procesado de trozos (hash, cadena, firma) en orden de llegada. */
  private queue: Promise<void> = Promise.resolve();
  private lastT = -1;
  private started = false;
  private result: Promise<CaptureVideo | null> | null = null;

  constructor(
    private readonly source: ChunkSource,
    private readonly opts: VideoRecorderOptions,
  ) {
    this.chainP = HashChain.forVideo(opts.sessionId, opts.serverNonce, opts.kind);
  }

  /** Tipo MIME de los trozos. */
  get mimeType(): string {
    return this.source.mimeType;
  }

  /** Trozos grabados hasta ahora. */
  get recorded(): readonly RecordedChunk[] {
    return this.chunks;
  }

  start(): void {
    if (this.started) throw new Error('recorder_already_started');
    this.started = true;
    this.source.start((bytes, t) => {
      this.queue = this.queue.then(() => this.take(bytes, t));
    }, this.opts.timesliceMs ?? 1000);
  }

  /**
   * Para la grabación, firma la raíz y devuelve el vídeo (o `null` si no hubo ningún trozo).
   * Llamarla varias veces devuelve el mismo resultado.
   */
  stop(): Promise<CaptureVideo | null> {
    if (!this.started) return Promise.resolve(null);
    this.result ??= this.finish();
    return this.result;
  }

  private async finish(): Promise<CaptureVideo | null> {
    await this.source.stop();
    await this.queue;
    const n = this.chunks.length;
    if (n === 0) return null;
    const chain = await this.chainP;
    const root = chain.root;
    const signatures = this.periodic.filter((s) => s.upto < n - 1);
    signatures.push({ upto: n - 1, hash: root, signature: await this.signAt(n - 1, root) });
    return {
      kind: this.opts.kind,
      chunks: this.chunks.map((c) => c.meta),
      chain_root: root,
      signatures,
    };
  }

  private async take(bytes: Uint8Array, t: number): Promise<void> {
    if (bytes.length === 0) return;
    // t_mono estrictamente creciente aunque el reloj del navegador agrupe eventos.
    const t_mono = Math.max(Math.round(t), this.lastT + 1);
    this.lastT = t_mono;
    const meta: CaptureVideoChunk = {
      idx: this.chunks.length,
      t_mono,
      sha256: await sha256Hex(bytes),
    };
    const chain = await this.chainP;
    const hash = await chain.push(meta);
    const chunk = { meta, bytes };
    this.chunks.push(chunk);
    if ((meta.idx + 1) % VIDEO_SIGN_EVERY === 0)
      this.periodic.push({ upto: meta.idx, hash, signature: await this.signAt(meta.idx, hash) });
    this.opts.onChunk?.(chunk);
  }

  private signAt(upto: number, hash: string): Promise<string> {
    return this.opts.signer.sign(
      videoMessage({
        session_id: this.opts.sessionId,
        server_nonce: this.opts.serverNonce,
        kind: this.opts.kind,
        upto,
        hash,
      }),
    );
  }
}

/** MIME preferido: `video/webm;codecs=vp8`; si no, el primero soportado o el del navegador. */
export function pickVideoMime(
  isSupported: (mime: string) => boolean = (m) =>
    typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m),
): string {
  for (const m of ['video/webm;codecs=vp8', 'video/webm', 'video/mp4'])
    if (isSupported(m)) return m;
  return '';
}

/** `ChunkSource` del navegador sobre `MediaRecorder` con `timeslice`. */
export class MediaRecorderChunkSource implements ChunkSource {
  private recorder: MediaRecorder | null = null;
  /** Lectura de los trozos en orden de llegada. */
  private tail: Promise<void> = Promise.resolve();
  readonly mimeType: string;

  constructor(
    private readonly stream: MediaStream,
    private readonly clock: CaptureClock,
    mime = pickVideoMime(),
  ) {
    this.mimeType = mime;
  }

  start(onChunk: (bytes: Uint8Array, t_mono: number) => void, timesliceMs: number): void {
    const rec = new MediaRecorder(
      this.stream,
      this.mimeType ? { mimeType: this.mimeType } : undefined,
    );
    rec.ondataavailable = (ev: BlobEvent) => {
      const t = this.clock.mono();
      if (!ev.data || ev.data.size === 0) return;
      const data = ev.data;
      this.tail = this.tail
        .then(() => data.arrayBuffer())
        .then((buf) => onChunk(new Uint8Array(buf), t));
    };
    this.recorder = rec;
    rec.start(timesliceMs);
  }

  stop(): Promise<void> {
    const rec = this.recorder;
    if (!rec || rec.state === 'inactive') return this.tail;
    return new Promise((resolve) => {
      rec.addEventListener(
        'stop',
        () => {
          // `dataavailable` final llega antes que `stop`; esperamos a que se lea su contenido.
          void this.tail.then(() => resolve());
        },
        { once: true },
      );
      rec.stop();
    });
  }
}
